import { appConfig } from '../config';
import type {
  ForceBankSettlementRequest,
  ForceBankSettlementResponse,
  RetrieveBankConfirmationRequest,
  BankConfirmationSlipResult,
  SettlementRailCatalogue,
} from './types';

export async function getSettlementRails(koreId: string): Promise<SettlementRailCatalogue> {
  const res = await fetch(`${appConfig.apiBaseUrl}/api/settlement/rails?koreId=${encodeURIComponent(koreId)}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  const body = await res.json() as SettlementRailCatalogue;
  if (!res.ok) throw new Error(`Rail catalogue unavailable (${res.status})`);
  return body;
}

async function post<TBody, TResult>(path: string, body: TBody, correlationId: string): Promise<TResult> {
  const serializedBody = JSON.stringify(body);
  const res = await fetch(`${appConfig.apiBaseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Correlation-Id': correlationId,
      // Idempotency-Key travels in the body; also sent as a header for gateway visibility.
    },
    body: serializedBody,
    credentials: 'same-origin',
  });

  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // keep raw text
  }

  if (!res.ok) {
    // The backend returns the real downstream body for FAILED/REJECTED — surface it.
    const err = new Error(`API ${res.status}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  return parsed as TResult;
}

export function newUuid(): string {
  return crypto.randomUUID();
}

export function forceBankResettleResync(
  request: ForceBankSettlementRequest,
): Promise<ForceBankSettlementResponse> {
  return post('/api/settlement/force-bank-resettle-resync', request, request.correlationId);
}

export function retrieveBankConfirmation(
  request: RetrieveBankConfirmationRequest,
): Promise<BankConfirmationSlipResult> {
  return post('/api/settlement/retrieve-bank-confirmation', request, request.correlationId);
}

// --- Per-payment approvals queue (maker-checker). The console surfaces + collects; the
// force-settle executor independently re-enforces a matching Approved entry server-side. ---

export interface ApprovalQueueItem {
  id: string;
  requestId: string;
  status: string;
  requestedBy?: string;
  approvedBy?: string;
  amount: string;
  currency: string;
  beneficiary?: string;
  beneficiaryAccount?: string;
  createdAt?: string;
  expiresAt?: string;
  complianceSummary?: string;
  blockers?: string[];
  auditTrail?: string[];
}

export interface CreateApprovalRequest {
  paymentId: string;
  uetr?: string;
  amount: number;
  currency: string;
  beneficiaryAccount: string;
  fromVaultId?: string;
  requiredApprovers: string[];
  requiredApprovalCount: number;
  expirationHours?: number;
}

export async function getApprovalsQueue(): Promise<ApprovalQueueItem[]> {
  const res = await fetch(`${appConfig.apiBaseUrl}/api/settlement/approvals/queue`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Approvals queue unavailable (${res.status})`);
  return (await res.json()) as ApprovalQueueItem[];
}

export function requestPaymentApproval(request: CreateApprovalRequest): Promise<ApprovalQueueItem> {
  return post('/api/settlement/approvals/request', request, newUuid());
}

export function approvePaymentRequest(requestId: string, reason: string): Promise<ApprovalQueueItem> {
  return post(`/api/settlement/approvals/${encodeURIComponent(requestId)}/approve`, { reason }, newUuid());
}
